import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import AthleteCard from './AthleteCard.jsx';
import './AthleteProfileHub.css';

// Chart panels pull in Observable Plot + d3 (~100KB gzip) — lazy-load them so
// that weight only downloads when a viewer actually opens Performance/Wellness.
const AthletePerformancePanel = lazy(() => import('./profile/AthletePerformancePanel.jsx'));
const AthleteWellnessPanel = lazy(() => import('./profile/AthleteWellnessPanel.jsx'));
const AthleteVideoCalendar = lazy(() => import('./AthleteVideoCalendar.js'));

// One role-scoped destination for a single athlete. Fetches the card once and
// shares it: Overview reuses the existing AthleteCard, Performance & Wellness are
// chart panels. Access (staff → any team athlete, parent → child, athlete → self)
// is enforced server-side by /api/athletes/:id/card and each panel's endpoint.
// (Distinct from the older AthleteProfile.js "full profile" page at /athlete/:id.)
const TABS = [
  { key: 'overview', label: 'Overview', icon: '📋' },
  { key: 'performance', label: 'Performance', icon: '📊' },
  { key: 'wellness', label: 'Wellness', icon: '🧘' },
  { key: 'videos', label: 'Videos', icon: '🎥' },
];

// Plain-language takeaways from the card data — so a coach gets the answer, not
// just charts. Returns up to 3 {sev, text} chips (sev: bad|warn|good).
function buildInsights(data) {
  const out = [];
  const att = data.attendance;
  if (att?.rate != null && att.total >= 4) {
    if (att.rate < 75) out.push({ sev: att.rate < 60 ? 'bad' : 'warn', text: `Attendance ${att.rate}%` });
    const recs = att.records || [];
    if (recs.length >= 6) {
      const half = Math.floor(recs.length / 2);
      const rate = (arr) => (arr.length ? Math.round(arr.filter(r => r.attended).length / arr.length * 100) : 0);
      const d = rate(recs.slice(half)) - rate(recs.slice(0, half));
      if (d <= -12) out.push({ sev: 'warn', text: `Attendance ↓${Math.abs(d)}%` });
      else if (d >= 12 && att.rate >= 85) out.push({ sev: 'good', text: `Attendance ↑${d}%` });
    }
  }
  const rec = (data.wellness?.recent || []).slice().sort((a, b) => new Date(b.checkin_date) - new Date(a.checkin_date));
  const latest = rec[0];
  if (latest) {
    if (latest.readiness_score != null && latest.readiness_score < 50) out.push({ sev: 'bad', text: `Readiness ${latest.readiness_score}` });
    if (latest.soreness != null && latest.soreness >= 4) out.push({ sev: 'warn', text: `Soreness ${latest.soreness}/5` });
  }
  const overdue = (data.billing?.outstanding || 0) > 0;
  if (overdue) out.push({ sev: 'warn', text: `$${Number(data.billing.outstanding).toFixed(0)} due` });
  return { chips: out.slice(0, 3), hasData: !!(att?.total || rec.length) };
}

export default function AthleteProfileHub({ athleteId: propId }) {
  const params = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const rawId = propId || params.id;
  // Athletes reach their own profile via /athlete-card/me (the nav doesn't know
  // their numeric athletes.id) — resolve it here.
  const [id, setId] = useState(rawId === 'me' ? null : rawId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const requested = search.get('tab') || 'overview';
  const active = TABS.some(t => t.key === requested) ? requested : 'overview';
  const deepLinkDate = search.get('date') || undefined; // /athlete-card/:id?tab=videos&date=YYYY-MM-DD
  const setTab = (key) => setSearch(key === 'overview' ? {} : { tab: key }, { replace: true });

  useEffect(() => {
    if (rawId !== 'me') { setId(rawId); return undefined; }
    let alive = true;
    api.get(`${API_BASE_URL}/api/athlete/me`)
      .then(res => { if (alive) setId(res.data?.id ?? null); })
      .catch(() => { if (alive) { setError('Could not find your athlete profile.'); setLoading(false); } });
    return () => { alive = false; };
  }, [rawId]);

  useEffect(() => {
    if (id == null) return undefined;
    let alive = true;
    setLoading(true); setError('');
    api.get(`${API_BASE_URL}/api/athletes/${id}/card`)
      .then(res => { if (alive) setData(res.data); })
      .catch(err => { if (alive) setError(err.response?.status === 403 ? "You don't have access to this athlete." : (err.response?.data?.error || 'Could not load this athlete.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div className="apf-wrap"><div className="apf-status">Loading athlete…</div></div>;
  if (error) return <div className="apf-wrap"><div className="apf-status err">{error}</div></div>;
  if (!data) return null;

  const name = data.athlete?.name || 'Athlete';
  const insights = buildInsights(data);

  return (
    <div className="apf-wrap">
      {(insights.chips.length > 0 || insights.hasData) && (
        <div className="apf-insight">
          {insights.chips.length === 0
            ? <span className="apf-chip good">✅ On track</span>
            : insights.chips.map((c, i) => <span key={i} className={`apf-chip ${c.sev}`}>{c.text}</span>)}
        </div>
      )}
      <div className="apf-bar">
        <button className="apf-back" onClick={() => navigate(-1)}>← Back</button>
        <span className="apf-name">{name}</span>
        <div className="apf-tabs" role="tablist">
          {TABS.map(t => (
            <button key={t.key} role="tab" aria-selected={active === t.key}
              className={`apf-tab ${active === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              <span className="apf-tab-ico">{t.icon}</span><span className="apf-tab-label">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="apf-panel">
        {active === 'overview' && <AthleteCard athleteId={id} initialData={data} embedded />}
        <Suspense fallback={<div className="apf-status">Loading…</div>}>
          {active === 'performance' && <AthletePerformancePanel athleteId={id} goals={data.goals?.items || []} attendance={data.attendance || null} />}
          {active === 'wellness' && <AthleteWellnessPanel athleteId={id} checkins={data.checkins || []} />}
          {active === 'videos' && <AthleteVideoCalendar athleteId={id} initialDate={deepLinkDate} />}
        </Suspense>
      </div>
    </div>
  );
}
